/**
 * Skills, full screen (S-06 rebuilt).
 *
 * A skill is a long text an agent reads — and long texts were being edited in
 * six-row boxes inside an agent card. This screen is the writing surface:
 * pick an agent (or all of them), pick a skill, and the text takes the whole
 * screen. The card's Skills section stays for ticking who reads what; this is
 * where the words get written.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';

import type { SkillContentView } from '../../client/types.js';
import { useConsoleMutation, useRoles, useSkillContents } from '../queries.js';
import { QueryPhase } from '../components/state.js';
import { Button } from '@primer/react';

const EVERYONE = '__all__';

export function SkillsScreen(): ReactNode {
  const skills = useSkillContents();
  const roles = useRoles();

  const agents = useMemo(
    () => (roles.data ?? []).map((role) => role.roleId).filter((id) => id !== 'main-operator'),
    [roles.data],
  );

  const [agent, setAgent] = useState<string>(EVERYONE);
  const [openRef, setOpenRef] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftText, setDraftText] = useState('');

  const invalidate = [['skill-contents'], ['skills']] as const;
  const save = useConsoleMutation<{ ref: string; name: string; text: string; enabled?: boolean }, void>(
    (data, context, args) => data.saveSkill(args.ref, args.name, args.text, context, args.enabled),
    invalidate,
  );
  const scope = useConsoleMutation<{ ref: string; roles: readonly string[] }, void>(
    (data, context, args) => data.setSkillRoles(args.ref, args.roles, context),
    invalidate,
  );
  const remove = useConsoleMutation<string, void>((data, context, ref) => data.resetSkill(ref, context), invalidate);

  const list = useMemo(() => {
    const all = skills.data ?? [];
    if (agent === EVERYONE) return all;
    return all.filter((skill) => skill.enabled && (skill.roles.length === 0 || skill.roles.includes(agent)));
  }, [skills.data, agent]);

  const open = list.find((skill) => skill.ref === openRef) ?? null;

  /* The editor loads the selected skill once; typing must never be clobbered
   * by a background refetch, so the draft only reseeds when the ref changes. */
  useEffect(() => {
    if (open === null) return;
    setDraftName(open.name);
    setDraftText(open.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRef, open === null]);

  useEffect(() => {
    if (openRef === null && list.length > 0) setOpenRef(list[0]?.ref ?? null);
    if (openRef !== null && open === null && list.length > 0) setOpenRef(list[0]?.ref ?? null);
  }, [list, openRef, open]);

  const startNew = (): void => {
    const name = 'new skill';
    const slugBase = `custom/skill-${String(Date.now() % 100000)}`;
    save.mutate(
      { ref: slugBase, name, text: 'What the agent should know.\n' },
      {
        onSuccess: () => {
          if (agent !== EVERYONE) scope.mutate({ ref: slugBase, roles: [agent] });
          setOpenRef(slugBase);
        },
      },
    );
  };

  const dirty = open !== null && (draftName !== open.name || draftText !== open.text);

  return (
    <div className="skills-screen">
      <aside className="skills-side">
        <label className="skills-agent">
          <span>Agent</span>
          <select value={agent} onChange={(event) => setAgent(event.target.value)}>
            <option value={EVERYONE}>all skills</option>
            {agents.map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </select>
        </label>

        <QueryPhase isPending={skills.isPending} error={skills.error} source="skills" rows={4} />
        <ul className="skills-list">
          {list.map((skill) => (
            <li key={skill.ref}>
              <button
                type="button"
                className={skill.ref === openRef ? 'skills-item skills-item-active' : 'skills-item'}
                onClick={() => setOpenRef(skill.ref)}
              >
                <span className="skills-item-name">{skill.name}</span>
                <span className="skills-item-scope">
                  {!skill.enabled
                    ? 'off'
                    : skill.roles.length === 0
                      ? 'every agent'
                      : skill.roles.join(', ')}
                </span>
              </button>
            </li>
          ))}
        </ul>
        <Button onClick={startNew} disabled={save.isPending}>
          {agent === EVERYONE ? '+ new skill' : `+ new skill for ${agent}`}
        </Button>
      </aside>

      <section className="skills-main" aria-label="Skill editor">
        {open === null ? (
          <p className="placeholder-inline">Pick a skill on the left, or create one.</p>
        ) : (
          <>
            <header className="skills-head">
              <input
                className="editor-title"
                value={draftName}
                onChange={(event) => setDraftName(event.target.value)}
                aria-label="skill name"
              />
              <span className="skills-ref">{open.ref}</span>
              <span className="skills-actions">
                <Button variant="primary"
                  disabled={!dirty || save.isPending}
                  onClick={() => save.mutate({ ref: open.ref, name: draftName.trim() || open.name, text: draftText })}
                >
                  {save.isPending ? 'Saving…' : dirty ? 'Save' : 'Saved'}
                </Button>
                {open.source === 'edited' ? (
                  <Button
                    disabled={remove.isPending}
                    onClick={() => {
                      remove.mutate(open.ref);
                      setOpenRef(null);
                    }}
                  >
                    {open.ref.startsWith('custom/') ? 'Delete' : 'Reset to built-in'}
                  </Button>
                ) : null}
              </span>
            </header>

            <Audience skill={open} agents={agents} scope={scope} save={save} />

            <textarea
              className="skills-text"
              value={draftText}
              onChange={(event) => setDraftText(event.target.value)}
              spellCheck={false}
            />
            {save.isError ? <p className="repo-probe repo-probe-bad">{String(save.error).slice(0, 160)}</p> : null}
            {scope.isError ? <p className="repo-probe repo-probe-bad">{String(scope.error).slice(0, 160)}</p> : null}
            <p className="hint">
              Edits land live: each agent home carries this text as a file, and the next message reads the new
              version. Saving here changes what the ticked agents know — it grants no permissions.
            </p>
          </>
        )}
      </section>
    </div>
  );
}

/** Who reads this skill — same semantics as the agent card, one line. */
function Audience({
  skill,
  agents,
  scope,
  save,
}: {
  skill: SkillContentView;
  agents: readonly string[];
  scope: { mutate: (args: { ref: string; roles: readonly string[] }) => void; isPending: boolean };
  save: {
    mutate: (args: { ref: string; name: string; text: string; enabled?: boolean }) => void;
    isPending: boolean;
  };
}): ReactNode {
  if (agents.length === 0) return null;
  return (
    <div className="skills-audience">
      <span className="skills-audience-label">read by</span>
      {agents.map((id) => {
        const mine = skill.enabled && (skill.roles.length === 0 || skill.roles.includes(id));
        return (
          <label key={id} className="check">
            <input
              type="checkbox"
              checked={mine}
              disabled={scope.isPending || save.isPending}
              onChange={(event) => {
                if (event.target.checked) {
                  if (!skill.enabled) {
                    save.mutate({ ref: skill.ref, name: skill.name, text: skill.text, enabled: true });
                    scope.mutate({ ref: skill.ref, roles: [id] });
                    return;
                  }
                  scope.mutate({ ref: skill.ref, roles: skill.roles.length === 0 ? [] : [...skill.roles, id] });
                  return;
                }
                const rest =
                  skill.roles.length === 0 ? agents.filter((entry) => entry !== id) : skill.roles.filter((entry) => entry !== id);
                if (rest.length === 0) {
                  save.mutate({ ref: skill.ref, name: skill.name, text: skill.text, enabled: false });
                  return;
                }
                scope.mutate({ ref: skill.ref, roles: rest });
              }}
            />
            {id}
          </label>
        );
      })}
    </div>
  );
}
